import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import posthog from 'posthog-js';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ExerciseLayout from './ExerciseLayout';
import ExerciseHeader from './ExerciseHeader';
import { progressService } from '../../services/progressService';
import CourseDashboard from './CourseDashboard';
import Skeleton from '../../components/ui/Skeleton';

// Import case metadata for intro screens
import { sqlFundamentalsMetadata, sqlFundamentalsChallenges } from './config/challenges/sqlFundamentalsChallenges';
import { sqlIntermediateMetadata, sqlIntermediateChallenges } from './config/challenges/sqlIntermediateChallenges';
import { paperPlusMetadata, paperPlusChallenges } from './config/challenges/paperPlusChallenges';
import { productMixMetadata, productMixChallenges } from './config/challenges/productMixChallenges';
import { analyticsMetadata, analyticsChallenges } from './config/challenges/analyticsChallenges';
import { fintechMetadata, fintechChallenges } from './config/challenges/fintechChallenges';

// Lazy load case components for better performance
const SQLFundamentalsCase = lazy(() => import('./cases/SQLFundamentalsCase'));
const SQLIntermediateCase = lazy(() => import('./cases/SQLIntermediateCase'));
const PaperPlusCase = lazy(() => import('./cases/PaperPlusCase'));
const ProductMixCase = lazy(() => import('./cases/ProductMixCase'));
const ProductAnalyticsCase = lazy(() => import('./cases/ProductAnalyticsCase'));
const FintechCase = lazy(() => import('./cases/FintechCase'));

// Case metadata for navigation and progress tracking
const CASE_METADATA = [
  {
    ...sqlFundamentalsMetadata,
    component: SQLFundamentalsCase,
    challengeCount: sqlFundamentalsChallenges.length,
    hasSQL: true,
    challenges: sqlFundamentalsChallenges,
    duration: '45 mins'
  },
  {
    ...sqlIntermediateMetadata,
    component: SQLIntermediateCase,
    challengeCount: sqlIntermediateChallenges.length,
    hasSQL: true,
    challenges: sqlIntermediateChallenges,
    duration: '60 mins'
  },
  {
    ...paperPlusMetadata,
    component: PaperPlusCase,
    challengeCount: paperPlusChallenges.length,
    hasSQL: true,
    challenges: paperPlusChallenges,
    duration: '60 mins'
  },
  {
    ...productMixMetadata,
    component: ProductMixCase,
    challengeCount: productMixChallenges.length,
    hasSQL: true,
    challenges: productMixChallenges,
    duration: '60 mins'
  },
  {
    ...analyticsMetadata,
    component: ProductAnalyticsCase,
    challengeCount: analyticsChallenges.length,
    hasSQL: true,
    challenges: analyticsChallenges,
    duration: '60 mins'
  },
  {
    ...fintechMetadata,
    component: FintechCase,
    challengeCount: fintechChallenges.length,
    hasSQL: true,
    challenges: fintechChallenges,
    duration: '60 mins'
  }
];

// Case metadata (CASE_METADATA definition remains the same)

const ThinkingInData = () => {
  const { user } = useAuth();
  const { caseIndex, challengeIndex } = useParams();
  const navigate = useNavigate();

  // Determine current case index. 
  // If undefined/invalid, return -1 to signal "Dashboard Mode"
  const currentCase = useMemo(() => {
    if (caseIndex === undefined || caseIndex === null) return -1;
    const idx = Number(caseIndex);
    return Number.isNaN(idx) ? -1 : idx;
  }, [caseIndex]);

  // Initialize currentChallenge from URL params
  const [currentChallenge, setCurrentChallenge] = useState(() => {
    const idx = Number(challengeIndex);
    return Number.isNaN(idx) || idx < 0 ? 0 : idx;
  });

  // Restore challengeView from localStorage logic... (mostly for when inside a case)
  const [challengeView, setChallengeView] = useState(() => {
    try {
      // If we are on dashboard, default to intro if we switch to a case? 
      // Actually standardizing on 'intro' is safe.
      const saved = localStorage.getItem('thinking-in-data-view');
      return saved || 'intro';
    } catch (e) {
      return 'intro';
    }
  });

  // ... (Progress loading logic: initialLocal, completedCases, etc. remains same)
  // Initialize completed cases from localStorage synchronously
  const initialLocal = (() => {
    try {
      const saved = localStorage.getItem('thinking-in-dataProgress');
      if (saved) {
        const parsed = JSON.parse(saved);
        return new Set(parsed.completed || []);
      }
    } catch (e) {
      // ignore
    }
    return new Set();
  })();

  const [completedCases, setCompletedCases] = useState(initialLocal);
  const [completedChallenges, setCompletedChallenges] = useState({});
  const [loading, setLoading] = useState(true);
  const [showHints, setShowHints] = useState({});
  const [showHints2, setShowHints2] = useState({});

  // ... (Toggle hints logic remains same)
  const toggleHint = (id) => {
    setShowHints(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleHint2 = (id) => {
    setShowHints2(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Sync URL params with state when URL changes
  useEffect(() => {
    // Only sync if we are IN a case
    if (currentCase !== -1) {
      const urlChallengeIndex = Number(challengeIndex);
      if (!Number.isNaN(urlChallengeIndex) && urlChallengeIndex >= 0) {
        setCurrentChallenge(urlChallengeIndex);
        setChallengeView('challenge');
      } else {
        setChallengeView('intro');
      }
    }
  }, [challengeIndex, currentCase]);

  // Save current challenge position (only if inside a case)
  useEffect(() => {
    if (currentCase !== -1) {
      localStorage.setItem('thinking-in-data-position', JSON.stringify({
        caseIndex: currentCase,
        challengeIndex: currentChallenge
      }));
      localStorage.setItem('thinking-in-data-view', challengeView);
    }
  }, [currentCase, currentChallenge, challengeView]);

  // Record challenge start time when a challenge is first opened
  useEffect(() => {
    if (currentCase < 0 || challengeView !== 'challenge') return;
    const caseData = CASE_METADATA[currentCase];
    if (!caseData) return;
    const challenge = caseData.challenges?.[currentChallenge];
    if (!challenge) return;
    progressService.startChallenge('thinking-in-data', caseData.id, challenge.id);
  }, [currentCase, currentChallenge, challengeView]);

  // Track if we have performed initial load
  const [dataLoaded, setDataLoaded] = useState(false);

  // Load progress from database (Keep this! Dashboard needs it)
  useEffect(() => {
    // If we already loaded data and user is same, don't show spinner
    // Just allow background update if needed
    const loadProgress = async () => {
      if (!dataLoaded) setLoading(true);
      try {
        const progress = await progressService.loadExerciseProgress('thinking-in-data');
        setCompletedCases(prev => {
          const combined = new Set(prev);
          (progress.completedCases || []).forEach(id => combined.add(id));
          return combined;
        });
        // Filter each case's challenge list to only IDs that exist in the current config.
        // This silently discards stale DB rows (renamed/removed challenges) so counts
        // can never exceed challengeCount and progress can never exceed 100%.
        const rawChallenges = progress.completedChallenges || {};
        const filtered = {};
        CASE_METADATA.forEach(meta => {
          const key = `case_${meta.id}`;
          const validIds = new Set(meta.challenges.map(c => c.id));
          const raw = rawChallenges[key] || [];
          filtered[key] = [...new Set(raw.filter(id => validIds.has(id)))];
        });
        setCompletedChallenges(filtered);
        setDataLoaded(true);
      } catch (error) {
        console.error('Error loading progress:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadProgress();
    } else {
      setLoading(false);
      setDataLoaded(false);
    }
    // We strictly depend on user.uid to avoid re-running on token refresh
    // But since `user` object might change reference, we check ID inside or use stable ID
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Save progress (Keep this!)
  useEffect(() => {
    if (loading) return;
    const saveProgress = async () => {
      const progress = {
        completedCases: Array.from(completedCases),
        completedChallenges,
        totalCases: CASE_METADATA.length
      };
      await progressService.saveExerciseProgress('thinking-in-data', progress);
    };
    saveProgress();
  }, [completedCases, completedChallenges, loading]);


  // Handlers
  const handleStartCase = (index) => {
    const caseData = CASE_METADATA[index];
    let targetChallengeIndex = 0;

    // Resume Logic: Find the first incomplete challenge
    if (caseData && caseData.challenges) {
      const caseKey = `case_${caseData.id}`;
      const completedForCase = completedChallenges[caseKey] || [];

      // Find the first challenge that is NOT completed
      const firstIncompleteIndex = caseData.challenges.findIndex(ch => !completedForCase.includes(ch.id));

      if (firstIncompleteIndex !== -1) {
        targetChallengeIndex = firstIncompleteIndex;
      }
      // If all are completed (index -1), default to 0 (review mode)
    }

    const targetUrl = `/exercises/thinking-in-data/case/${index}/challenge/${targetChallengeIndex}`;

    if (!user) {
      navigate('/login', { state: { from: targetUrl } });
      return;
    }
    navigate(targetUrl);
    // State will sync via URL useEffect
  };

  const calculateCompletionPercentage = () => {
    const totalCases = CASE_METADATA.length;
    if (totalCases === 0) return 0;

    // Filter completedCases to only include IDs that actually exist in metadata
    // This fixes the "150%" bug caused by legacy/ghost IDs in localStorage/DB
    const validCompletedCount = Array.from(completedCases).filter(id =>
      CASE_METADATA.some(meta => meta.id === id)
    ).length;

    return Math.round((validCompletedCount / totalCases) * 100);
  };

  const handleChallengeClick = (caseIndex, challengeIndex) => {
    if (!user) {
      navigate('/login', { state: { from: `/exercises/thinking-in-data/case/${caseIndex}/challenge/${challengeIndex}` } });
      return;
    }
    navigate(`/exercises/thinking-in-data/case/${caseIndex}/challenge/${challengeIndex}`);
  };

  // If currentCase is -1, show Dashboard
  if (currentCase === -1) {
    if (loading) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#0a1220] p-4 lg:p-8 transition-colors duration-300">
          <div className="max-w-7xl mx-auto space-y-8 animate-pulse">
            {/* Header Skeleton */}
            <div className="space-y-4">
              <Skeleton className="h-12 w-64 bg-gray-200 dark:bg-gray-800" />
              <Skeleton className="h-6 w-96 bg-gray-200 dark:bg-gray-800" />
            </div>

            {/* Progress Card Skeleton */}
            <Skeleton className="h-40 w-full rounded-2xl bg-gray-200 dark:bg-gray-800" />

            {/* Case Cards Grid Skeleton */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-80 w-full rounded-2xl bg-gray-200 dark:bg-gray-800" />
              ))}
            </div>
          </div>
        </div>
      );
    }

    const validCompletedCases = new Set(
      Array.from(completedCases).filter(id => CASE_METADATA.some(meta => meta.id === id))
    );

    return (
      <CourseDashboard
        cases={CASE_METADATA}
        completedCases={validCompletedCases}
        completedChallenges={completedChallenges}
        onStartCase={handleStartCase}
        onChallengeClick={handleChallengeClick}
        completionPercentage={calculateCompletionPercentage()}
        accentColor="orange"
      />
    );
  }

  // --- EXISTING LOGIC FOR SPECIFIC CASE ---

  const currentCaseData = CASE_METADATA[currentCase];
  // Safety check if index invalid
  if (!currentCaseData) {
    // Redirect back to dashboard if invalid index
    // Use useEffect to avoid render-phase navigation warning?
    return <div className="p-8">Case not found. <button onClick={() => navigate('/exercises/thinking-in-data')} className="text-blue-500 underline">Return to Dashboard</button></div>;
  }

  // Navigation handlers (Keep existing logic, updated for context)
  const handleNextChallenge = () => {
    if (challengeView === 'intro') {
      navigate(`/exercises/thinking-in-data/case/${currentCase}/challenge/0`);
      // ...
    } else {
      if (currentChallenge < currentCaseData.challengeCount - 1) {
        const nextChallenge = currentChallenge + 1;
        navigate(`/exercises/thinking-in-data/case/${currentCase}/challenge/${nextChallenge}`);
      } else if (currentCase < CASE_METADATA.length - 1) {
        const nextCase = currentCase + 1;
        navigate(`/exercises/thinking-in-data/case/${nextCase}`);
        // Intro triggers automatically via URL
      } else {
        // Course Finished? Back to dashboard
        navigate('/exercises/thinking-in-data');
      }
    }
  };

  const handlePrevChallenge = () => {
    if (currentChallenge > 0) {
      const prevChallenge = currentChallenge - 1;
      navigate(`/exercises/thinking-in-data/case/${currentCase}/challenge/${prevChallenge}`);
    } else {
      // Go back to intro
      navigate(`/exercises/thinking-in-data/case/${currentCase}`);
    }
  };

  const handlePrevFromIntro = () => {
    if (currentCase > 0) {
      // Prev case
      const prevCaseIndex = currentCase - 1;
      const prevCaseData = CASE_METADATA[prevCaseIndex];
      // Navigate to the LAST challenge of the previous case, not the intro
      const lastChallengeIndex = prevCaseData ? prevCaseData.challengeCount - 1 : 0;

      navigate(`/exercises/thinking-in-data/case/${prevCaseIndex}/challenge/${lastChallengeIndex}`);
    } else {
      // Back to dashboard
      navigate('/exercises/thinking-in-data');
    }
  };

  // ... (Other handlers: handleCaseChange, handleChallengeClick, handleChallengeComplete remain same)
  // We need to ensure they are available in this scope.
  // Re-declaring for clarity or ensuring existing function bodies are preserved below.

  const handleCaseChange = (newCaseIndex) => {
    // Allow viewing the case intro without login
    navigate(`/exercises/thinking-in-data/case/${newCaseIndex}`);
  };



  const handleChallengeComplete = (challengeId) => {
    const caseKey = `case_${currentCaseData.id}`;

    console.log('✅ Challenge Completed:', challengeId, 'Case Key:', caseKey);

    posthog.capture('challenge_completed', {
      challenge_id: challengeId,
      case_id: currentCaseData.id,
      case_title: currentCaseData.title
    });

    // 1. Optimistic Update (UI tickmark)
    setCompletedChallenges(prev => {
      const caseChallenges = prev[caseKey] || [];
      if (caseChallenges.includes(challengeId)) return prev;

      const newState = {
        ...prev,
        [caseKey]: [...caseChallenges, challengeId]
      };
      console.log('🔄 New Completed State:', newState);
      return newState;
    });

    // 2. Persist to Supabase/Service
    progressService.completeChallenge('thinking-in-data', currentCaseData.id, challengeId)
      .catch(err => console.error("Failed to save progress", err));

    // 3. Mark Case as Complete if all challenges done
    const currentChallenges = completedChallenges[caseKey] || [];
    // Note: Use current length + 1 because state update checks previous render
    // Or better, just check if we reached the count.
    // Safe approach: check if size + 1 >= total
    const isNewCompletion = !currentChallenges.includes(challengeId);

    if (isNewCompletion && currentChallenges.length + 1 >= currentCaseData.challengeCount) {
      if (!completedCases.has(currentCaseData.id)) {
        setCompletedCases(prev => {
          const newSet = new Set(prev);
          newSet.add(currentCaseData.id);
          return newSet;
        });

        progressService.completeCase('thinking-in-data', currentCaseData.id);

        posthog.capture('case_completed', {
          case_id: currentCaseData.id,
          case_title: currentCaseData.title
        });
      }
    }
  };

  // Helper for layout
  const completedCaseIndexes = new Set(
    Array.from(completedCases)
      .map(id => CASE_METADATA.findIndex(c => c.id === id))
      .filter(idx => idx !== -1)
  );

  const CaseComponent = currentCaseData?.component;

  return (
    <ExerciseLayout
      title="Thinking in Data"
      onTitleClick={() => navigate('/exercises/thinking-in-data')}
      cases={CASE_METADATA}
      currentCase={currentCase}
      onCaseChange={handleCaseChange}
      completedCases={completedCaseIndexes}
      completionPercentage={calculateCompletionPercentage()}
      completedChallenges={completedChallenges}
      onChallengeClick={handleChallengeClick}
      currentChallenge={challengeView === 'challenge' ? currentChallenge : -1}
      onPrevChallenge={challengeView === 'intro' ? handlePrevFromIntro : handlePrevChallenge}
      onNextChallenge={handleNextChallenge}
      canGoPrev={true} // Always allow going back (to dashboard or prev case)
      canGoNext={true}
      isIntro={challengeView === 'intro'}
    >
      {/* Case Content */}
      <div className="bg-white dark:bg-[#162032] rounded-xl shadow-lg overflow-hidden h-full flex flex-col border border-gray-200 dark:border-gray-800 transition-colors duration-300">
        {challengeView === 'intro' && (
          <ExerciseHeader
            title={currentCaseData.title}
            scenario={currentCaseData.scenario}
            introduction={currentCaseData.introduction}
          />
        )}

        <div className="flex-1 overflow-auto p-4 lg:p-6">
          {challengeView === 'intro' ? (
            <div className="text-center py-12">
              <button
                onClick={() => {
                  if (!user) {
                    navigate('/login', { state: { from: `/exercises/thinking-in-data/case/${currentCase}/challenge/0` } });
                    return;
                  }
                  navigate(`/exercises/thinking-in-data/case/${currentCase}/challenge/0`);
                }}
                className="px-8 py-4 bg-gradient-to-r from-[#f97316] to-[#ea580c] text-white text-lg font-bold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                {user ? 'Start Challenges →' : 'Login to Start Challenges →'}
              </button>
            </div>
          ) : (
            <Suspense fallback={
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            }>
              {CaseComponent && (() => {
                // Component render logic...
                const challengeIndex = currentCaseData.challengeCount > currentChallenge ? currentChallenge : 0;
                const caseKey = `case_${currentCaseData.id}`;

                // DATA FIX: Use the REAL challenge ID from the metadata array (e.g. 'select-all')
                // instead of generating 'challenge_0', which causes mismatch with the menu tickmarks.
                // Fallback to index-based ID only if challenges array is missing (safety).
                const realChallengeId = currentCaseData.challenges && currentCaseData.challenges[challengeIndex]
                  ? currentCaseData.challenges[challengeIndex].id
                  : `challenge_${challengeIndex}`;

                const isCompleted = (completedChallenges[caseKey] || []).includes(realChallengeId);

                return (
                  <CaseComponent
                    key={`case-${currentCase}`}
                    currentChallenge={challengeIndex}
                    exerciseId={currentCaseData.id}
                    onComplete={() => handleChallengeComplete(realChallengeId)}
                    showHint={showHints[realChallengeId]}
                    showHint2={showHints2[realChallengeId]}
                    showSolution={showHints[`solution_${realChallengeId}`]}
                    onToggleHint={() => toggleHint(realChallengeId)}
                    onToggleHint2={() => toggleHint2(realChallengeId)}
                    onToggleSolution={() => setShowHints(prev => ({
                      ...prev,
                      [`solution_${realChallengeId}`]: !prev[`solution_${realChallengeId}`]
                    }))}
                    isCompleted={isCompleted}
                    onNext={handleNextChallenge}
                  />
                );
              })()}
            </Suspense>
          )}
        </div>
      </div>
    </ExerciseLayout>
  );
};

export default ThinkingInData;
